<template>
  <div class="agent-page">
    <header class="agent-header">
      <div class="agent-header-inner">
        <button
          class="toolbar-button"
          type="button"
          aria-label="返回搜索页"
          @click="$router.push('/')"
        >
          <svg aria-hidden="true" viewBox="0 0 24 24">
            <path d="M19 12H5" />
            <path d="M12 19l-7-7 7-7" />
          </svg>
          <span>返回搜索</span>
        </button>

        <div class="agent-title">
          <span class="brand-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24">
              <path d="M12 4v3" />
              <path d="M7 8h10a4 4 0 0 1 4 4v2a4 4 0 0 1-4 4H9l-4 3v-3H7a4 4 0 0 1-4-4v-2a4 4 0 0 1 4-4z" />
              <path d="M9 13h.01" />
              <path d="M15 13h.01" />
            </svg>
          </span>
          <div>
            <h1>AI助手</h1>
            <p>影视资源检索与链接提取</p>
          </div>
        </div>

        <button
          class="toolbar-button toolbar-button-primary"
          type="button"
          :disabled="loading"
          aria-label="开始新对话"
          @click="clearConversation"
        >
          <svg aria-hidden="true" viewBox="0 0 24 24">
            <path d="M12 5v14" />
            <path d="M5 12h14" />
          </svg>
          <span>新对话</span>
        </button>
      </div>
    </header>

    <main class="agent-main" aria-label="AI助手对话">
      <section ref="conversation" class="conversation" aria-live="polite">
        <div v-if="messages.length === 0" class="welcome">
          <div class="welcome-mark" aria-hidden="true">
            <svg viewBox="0 0 24 24">
              <path d="M12 4v3" />
              <path d="M7 8h10a4 4 0 0 1 4 4v2a4 4 0 0 1-4 4H9l-4 3v-3H7a4 4 0 0 1-4-4v-2a4 4 0 0 1 4-4z" />
              <path d="M9 13h.01" />
              <path d="M15 13h.01" />
            </svg>
          </div>
          <h2>今天想找什么？</h2>
          <div class="suggestions">
            <button type="button" @click="useSuggestion('搜索最近上映的科幻电影')">
              <svg aria-hidden="true" viewBox="0 0 24 24">
                <path d="M21 21l-4.35-4.35" />
                <circle cx="11" cy="11" r="7" />
              </svg>
              <span>最近科幻电影</span>
            </button>
            <button type="button" @click="useSuggestion('搜索甄嬛传')">
              <svg aria-hidden="true" viewBox="0 0 24 24">
                <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
              </svg>
              <span>甄嬛传</span>
            </button>
          </div>

          <section class="hot-section" aria-label="今日热搜推荐">
            <div class="hot-heading">
              <div>
                <span>今日热搜</span>
                <h3>热门资源推荐</h3>
              </div>
              <span>点击搜索</span>
            </div>
            <div v-if="hotLoading" class="hot-state">正在加载今日热搜...</div>
            <div v-else-if="hotError" class="hot-state">{{ hotError }}</div>
            <div v-else-if="hotResources.length" class="hot-list">
              <button
                v-for="(item, index) in hotResources"
                :key="`${item.category}-${item.title}`"
                type="button"
                class="hot-item"
                @click="useSuggestion(`搜索${item.title}`)"
              >
                <span class="hot-rank">{{ index + 1 }}</span>
                <span class="hot-main">
                  <span class="hot-title">{{ item.title }}</span>
                  <span class="hot-category">{{ item.category }}</span>
                </span>
                <svg aria-hidden="true" viewBox="0 0 24 24">
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </button>
            </div>
            <div v-else class="hot-state">暂无今日热搜</div>
          </section>
        </div>

        <article
          v-for="(item, index) in messages"
          :key="`${item.role}-${index}`"
          class="message-row"
          :class="[item.role, { streaming: item.streaming }]"
        >
          <div class="message-avatar" :class="item.role" :aria-label="item.role === 'user' ? '我' : 'AI助手'">
            <span v-if="item.role === 'user'">我</span>
            <svg v-else aria-hidden="true" viewBox="0 0 24 24">
              <path d="M12 4v3" />
              <path d="M7 8h10a4 4 0 0 1 4 4v2a4 4 0 0 1-4 4H9l-4 3v-3H7a4 4 0 0 1-4-4v-2a4 4 0 0 1 4-4z" />
              <path d="M9 13h.01" />
              <path d="M15 13h.01" />
            </svg>
          </div>
          <div class="message-content">
            <div class="message-author">{{ item.role === 'user' ? '我' : 'AI助手' }}</div>
            <div class="message-bubble">
              <span v-if="item.streaming && !item.content" class="inline-loading" aria-label="AI助手正在输入">
                <span></span><span></span><span></span>
              </span>
              <template v-else>
                <template v-for="(segment, segmentIndex) in getMessageSegments(item)">
                  <span
                    v-if="segment.type === 'text'"
                    :key="`${item.role}-${index}-text-${segmentIndex}`"
                  >{{ segment.text }}</span>
                  <a
                    v-else-if="isMobile"
                    :key="`${item.role}-${index}-link-${segmentIndex}`"
                    class="message-link"
                    :href="segment.url"
                    target="_blank"
                    rel="noopener noreferrer"
                  >{{ segment.url }}</a>
                  <span
                    v-else
                    :key="`${item.role}-${index}-qr-${segmentIndex}`"
                    class="qr-link-card"
                  >
                    <span class="qr-image-wrap">
                      <img
                        v-if="segment.qrCode"
                        :src="segment.qrCode"
                        alt="可用链接二维码"
                        class="qr-image"
                      />
                      <span v-else class="qr-loading"></span>
                    </span>
                    <span class="qr-caption">扫码打开可用链接</span>
                  </span>
                </template>
                <span v-if="item.streaming" class="stream-cursor" aria-hidden="true"></span>
              </template>
            </div>
          </div>
        </article>
      </section>

      <form class="composer" aria-label="AI助手输入框" @submit.prevent="sendMessage">
        <span class="composer-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24">
            <path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z" />
          </svg>
        </span>
        <textarea
          ref="composerInput"
          v-model="input"
          rows="1"
          maxlength="1000"
          :disabled="loading"
          placeholder="输入片名、类型或要提取的序号"
          @input="resizeComposer"
          @compositionstart="handleCompositionStart"
          @compositionend="handleCompositionEnd"
          @keydown="handleComposerKeydown"
        ></textarea>
        <button class="send-button" type="submit" :disabled="loading || !input.trim()" aria-label="发送消息">
          <svg aria-hidden="true" viewBox="0 0 24 24">
            <path d="M22 2L11 13" />
            <path d="M22 2l-7 20-4-9-9-4 20-7z" />
          </svg>
          <span>发送</span>
        </button>
      </form>
    </main>
  </div>
</template>

<script>
import QRCode from 'qrcode';
import dailyResourcesService from '../services/dailyResources';

export default {
  name: 'Agent',
  data() {
    return {
      input: '',
      loading: false,
      sessionId: '',
      messages: [],
      hotResources: [],
      hotLoading: false,
      hotError: '',
      isMobile: false,
      isComposing: false,
      lastCompositionEndAt: 0,
      activeController: null
    };
  },
  mounted() {
    const storedSessionId = window.localStorage.getItem('movie-auto-agent-session');
    this.sessionId = storedSessionId || this.createSessionId();
    window.localStorage.setItem('movie-auto-agent-session', this.sessionId);
    this.checkMobile();
    window.addEventListener('resize', this.handleResize);
    this.loadHotResources();
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
    if (this.activeController) {
      this.activeController.abort();
    }
  },
  methods: {
    checkMobile() {
      this.isMobile = window.innerWidth < 768;
    },
    handleResize() {
      this.checkMobile();
    },
    createSessionId() {
      if (window.crypto && window.crypto.randomUUID) {
        return window.crypto.randomUUID();
      }
      return `agent-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    },
    useSuggestion(text) {
      this.input = text;
      this.sendMessage();
    },
    async loadHotResources() {
      this.hotLoading = true;
      this.hotError = '';

      try {
        const [movieResult, tvResult] = await Promise.all([
          dailyResourcesService.getDailyResources('movie'),
          dailyResourcesService.getDailyResources('tv')
        ]);
        const movieResources = movieResult.success
          ? (movieResult.data?.list || []).map(item => ({
            ...item,
            category: '电影'
          }))
          : [];
        const tvResources = tvResult.success
          ? (tvResult.data?.list || []).map(item => ({
            ...item,
            category: '电视剧'
          }))
          : [];
        const resources = [];
        const maxLength = Math.max(movieResources.length, tvResources.length);
        for (let index = 0; index < maxLength; index += 1) {
          if (movieResources[index]) resources.push(movieResources[index]);
          if (tvResources[index]) resources.push(tvResources[index]);
        }

        this.hotResources = resources
          .filter(item => item.title && item.title.trim())
          .filter((item, index, list) => list.findIndex(candidate => candidate.title === item.title) === index)
          .slice(0, 8);

        if (this.hotResources.length === 0 && !movieResult.success && !tvResult.success) {
          this.hotError = '每日热搜暂时不可用';
        }
      } catch (error) {
        console.error('加载AI助手每日热搜失败:', error);
        this.hotError = '每日热搜暂时不可用';
      } finally {
        this.hotLoading = false;
      }
    },
    clearConversation() {
      if (this.activeController) {
        this.activeController.abort();
        this.activeController = null;
      }
      this.messages = [];
      this.input = '';
      this.loading = false;
      this.sessionId = this.createSessionId();
      window.localStorage.setItem('movie-auto-agent-session', this.sessionId);
      this.$nextTick(() => {
        this.resizeComposer();
      });
    },
    resizeComposer(event) {
      const textarea = event?.target || this.$refs.composerInput;
      if (!textarea) return;

      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 128)}px`;
    },
    handleCompositionStart() {
      this.isComposing = true;
    },
    handleCompositionEnd(event) {
      this.isComposing = false;
      this.lastCompositionEndAt = Date.now();
      this.$nextTick(() => {
        this.resizeComposer(event);
      });
    },
    isImeEnterEvent(event) {
      return this.isComposing
        || event.isComposing
        || event.keyCode === 229
        || Date.now() - this.lastCompositionEndAt < 120;
    },
    handleComposerKeydown(event) {
      if (event.key !== 'Enter') {
        return;
      }

      if (this.isImeEnterEvent(event)) {
        return;
      }

      if (event.shiftKey || event.ctrlKey || event.metaKey || event.altKey) {
        this.$nextTick(() => {
          this.resizeComposer(event);
        });
        return;
      }

      event.preventDefault();
      this.sendMessage();
    },
    scrollConversation() {
      this.$nextTick(() => {
        const conversation = this.$refs.conversation;
        if (conversation) {
          conversation.scrollTop = conversation.scrollHeight;
        }
      });
    },
    extractMessageSegments(content) {
      const text = String(content || '');
      const urlPattern = /https?:\/\/[^\s<>"'，。！？、）\])]+/g;
      const segments = [];
      let lastIndex = 0;
      let match;

      while ((match = urlPattern.exec(text)) !== null) {
        if (match.index > lastIndex) {
          segments.push({
            type: 'text',
            text: text.slice(lastIndex, match.index)
          });
        }

        segments.push({
          type: 'link',
          url: match[0],
          qrCode: ''
        });
        lastIndex = match.index + match[0].length;
      }

      if (lastIndex < text.length) {
        segments.push({
          type: 'text',
          text: text.slice(lastIndex)
        });
      }

      return segments.length ? segments : [{ type: 'text', text }];
    },
    updateMessageSegments(message) {
      this.$set(message, 'segments', this.extractMessageSegments(message.content));
    },
    async buildMessage(role, content) {
      const message = {
        role,
        content,
        segments: this.extractMessageSegments(content),
        streaming: false
      };

      if (role === 'assistant') {
        await this.generateMessageQrCodes(message);
      }

      return message;
    },
    async generateMessageQrCodes(message) {
      if (!message || message.qrGenerating || !Array.isArray(message.segments)) {
        return;
      }

      message.qrGenerating = true;
      await Promise.all(message.segments.map(async (segment, index) => {
        if (segment.type !== 'link' || segment.qrCode) return;

        try {
          const qrCode = await QRCode.toDataURL(segment.url, {
            width: 180,
            height: 180,
            margin: 2,
            color: {
              dark: '#111827',
              light: '#ffffff'
            }
          });
          this.$set(message.segments, index, {
            ...segment,
            qrCode
          });
        } catch (error) {
          console.error('生成AI助手链接二维码失败:', error);
        }
      }));
      message.qrGenerating = false;
    },
    getMessageSegments(item) {
      if (Array.isArray(item.segments)) {
        return item.segments;
      }

      this.updateMessageSegments(item);
      if (item.role === 'assistant') {
        this.generateMessageQrCodes(item);
      }

      return item.segments;
    },
    parseSseFrame(frame) {
      const lines = String(frame || '').split('\n');
      let event = 'message';
      const dataLines = [];

      lines.forEach(line => {
        if (!line || line.startsWith(':')) return;
        if (line.startsWith('event:')) {
          event = line.slice(6).trim();
          return;
        }
        if (line.startsWith('data:')) {
          dataLines.push(line.slice(5).replace(/^ /, ''));
        }
      });

      if (dataLines.length === 0) {
        return { event, payload: null };
      }

      const data = dataLines.join('\n');
      try {
        return { event, payload: JSON.parse(data) };
      } catch (error) {
        return { event, payload: data };
      }
    },
    async handleSseFrame(frame, assistantMessage) {
      const { event, payload } = this.parseSseFrame(frame);
      if (!payload) return;

      if (event === 'session' && payload.session_id) {
        this.sessionId = payload.session_id;
        window.localStorage.setItem('movie-auto-agent-session', this.sessionId);
        return;
      }

      if (event === 'delta') {
        const content = payload.content || '';
        if (!content) return;

        this.$set(assistantMessage, 'content', `${assistantMessage.content}${content}`);
        this.updateMessageSegments(assistantMessage);
        this.scrollConversation();
        return;
      }

      if (event === 'done') {
        if (payload.session_id) {
          this.sessionId = payload.session_id;
          window.localStorage.setItem('movie-auto-agent-session', this.sessionId);
        }
        if (payload.message) {
          this.$set(assistantMessage, 'content', payload.message);
        }
        this.$set(assistantMessage, 'streaming', false);
        this.updateMessageSegments(assistantMessage);
        await this.generateMessageQrCodes(assistantMessage);
        this.scrollConversation();
        return;
      }

      if (event === 'error') {
        throw new Error(payload.message || 'AI助手暂时不可用');
      }
    },
    async readAgentStream(response, assistantMessage) {
      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        buffer += decoder.decode(value, { stream: !done });
        buffer = buffer.replace(/\r\n/g, '\n');

        let separatorIndex = buffer.indexOf('\n\n');
        while (separatorIndex !== -1) {
          const frame = buffer.slice(0, separatorIndex);
          buffer = buffer.slice(separatorIndex + 2);
          await this.handleSseFrame(frame, assistantMessage);
          separatorIndex = buffer.indexOf('\n\n');
        }

        if (done) {
          break;
        }
      }

      if (buffer.trim()) {
        await this.handleSseFrame(buffer, assistantMessage);
      }
    },
    async readErrorMessage(response) {
      try {
        const data = await response.json();
        return data.message || 'AI助手暂时不可用';
      } catch (error) {
        return 'AI助手暂时不可用';
      }
    },
    async sendMessage() {
      const content = this.input.trim();
      if (!content || this.loading) return;

      const userMessage = await this.buildMessage('user', content);
      this.messages.push(userMessage);
      this.input = '';
      this.resizeComposer();

      const assistantMessage = {
        role: 'assistant',
        content: '',
        segments: [{ type: 'text', text: '' }],
        streaming: true
      };
      this.messages.push(assistantMessage);
      this.loading = true;
      this.scrollConversation();

      const requestMessages = this.messages
        .filter(item => item !== assistantMessage)
        .map(item => ({
          role: item.role,
          content: item.content
        }));

      const controller = new AbortController();
      this.activeController = controller;

      try {
        const response = await fetch('/api/agent/chat/stream', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            session_id: this.sessionId,
            messages: requestMessages
          }),
          signal: controller.signal
        });

        if (!response.ok) {
          throw new Error(await this.readErrorMessage(response));
        }
        if (!response.body) {
          throw new Error('当前浏览器不支持流式输出');
        }

        await this.readAgentStream(response, assistantMessage);
        if (!assistantMessage.content) {
          this.$set(assistantMessage, 'content', '我暂时没有返回内容。');
          this.updateMessageSegments(assistantMessage);
        }
      } catch (error) {
        if (error.name === 'AbortError') {
          return;
        }

        const message = error.message || 'AI助手暂时不可用，请稍后重试';
        this.$set(assistantMessage, 'content', message);
        this.updateMessageSegments(assistantMessage);
        await this.generateMessageQrCodes(assistantMessage);
      } finally {
        if (this.activeController === controller) {
          this.activeController = null;
        }
        this.$set(assistantMessage, 'streaming', false);
        this.loading = false;
        this.scrollConversation();
      }
    }
  }
};
</script>

<style scoped>
.agent-page {
  --agent-bg: #101114;
  --agent-surface: #17191f;
  --agent-surface-soft: #1d2027;
  --agent-border: #2b3038;
  --agent-border-strong: #3a414c;
  --agent-text: #f4f6f8;
  --agent-muted: #9aa3af;
  --agent-soft-text: #c6ccd4;
  --agent-primary: #2f6fed;
  --agent-primary-hover: #3f7df2;
  --agent-accent: #2fb486;
  min-height: calc(100vh - 60px);
  background: var(--agent-bg);
  color: var(--agent-text);
}

.agent-header {
  position: sticky;
  top: 0;
  z-index: 10;
  background: rgba(16, 17, 20, .96);
  border-bottom: 1px solid var(--agent-border);
  backdrop-filter: blur(14px);
}

.agent-header-inner {
  width: min(1080px, calc(100% - 32px));
  margin: 0 auto;
  min-height: 72px;
  display: grid;
  grid-template-columns: minmax(128px, auto) 1fr minmax(128px, auto);
  align-items: center;
  gap: 16px;
}

.agent-title {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-width: 0;
}

.brand-icon,
.welcome-mark,
.message-avatar.assistant {
  display: grid;
  place-items: center;
  background: #1e2530;
  border: 1px solid #334155;
  color: #8cc7ff;
}

.brand-icon {
  width: 38px;
  height: 38px;
  border-radius: 8px;
}

.brand-icon svg,
.welcome-mark svg,
.message-avatar svg,
.toolbar-button svg,
.suggestions svg,
.hot-item svg,
.composer-icon svg,
.send-button svg {
  width: 18px;
  height: 18px;
  fill: none;
  stroke: currentColor;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.agent-title h1 {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  letter-spacing: 0;
}

.agent-title p {
  margin: 3px 0 0;
  color: var(--agent-muted);
  font-size: 13px;
}

.toolbar-button,
.suggestions button,
.hot-item,
.send-button {
  appearance: none;
  border: 1px solid var(--agent-border);
  border-radius: 8px;
  cursor: pointer;
  transition: border-color .18s ease, background .18s ease, color .18s ease, transform .18s ease;
}

.toolbar-button {
  min-height: 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 0 12px;
  background: #181b21;
  color: var(--agent-soft-text);
  font-size: 14px;
}

.toolbar-button:last-child {
  justify-self: end;
}

.toolbar-button:hover:not(:disabled),
.suggestions button:hover,
.hot-item:hover,
.send-button:hover:not(:disabled) {
  border-color: var(--agent-border-strong);
  background: #222730;
  color: #fff;
}

.toolbar-button:focus-visible,
.suggestions button:focus-visible,
.hot-item:focus-visible,
.send-button:focus-visible,
.composer textarea:focus-visible {
  outline: 2px solid rgba(47, 111, 237, .55);
  outline-offset: 2px;
}

.toolbar-button:disabled {
  opacity: .55;
  cursor: not-allowed;
}

.toolbar-button-primary {
  background: #1d2c46;
  border-color: #315179;
  color: #d7e7ff;
}

.toolbar-button-primary:hover:not(:disabled) {
  background: #244068;
  border-color: #3b6aa3;
}

.agent-main {
  width: min(1080px, calc(100% - 32px));
  margin: 0 auto;
}

.conversation {
  height: calc(100vh - 230px);
  min-height: 430px;
  overflow-y: auto;
  padding: 34px 2px 24px;
  scrollbar-color: #3a414c transparent;
}

.welcome {
  max-width: 760px;
  margin: 8vh auto 0;
  text-align: center;
}

.welcome-mark {
  width: 54px;
  height: 54px;
  margin: 0 auto 16px;
  border-radius: 8px;
}

.welcome-mark svg {
  width: 24px;
  height: 24px;
}

.welcome h2 {
  margin: 0;
  font-size: 26px;
  font-weight: 700;
  letter-spacing: 0;
}

.suggestions {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 22px;
}

.suggestions button {
  min-height: 38px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0 14px;
  background: var(--agent-surface);
  color: var(--agent-soft-text);
  font-size: 14px;
}

.hot-section {
  margin-top: 30px;
  text-align: left;
}

.hot-heading {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.hot-heading span {
  color: var(--agent-muted);
  font-size: 12px;
}

.hot-heading h3 {
  margin: 4px 0 0;
  color: var(--agent-text);
  font-size: 16px;
  font-weight: 650;
  letter-spacing: 0;
}

.hot-list {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.hot-item {
  display: grid;
  grid-template-columns: 28px minmax(0, 1fr) 18px;
  align-items: center;
  gap: 10px;
  min-width: 0;
  min-height: 56px;
  padding: 9px 12px;
  background: var(--agent-surface);
  color: var(--agent-soft-text);
  text-align: left;
}

.hot-rank {
  width: 24px;
  height: 24px;
  display: grid;
  place-items: center;
  border-radius: 6px;
  background: #232a34;
  color: #8cc7ff;
  font-size: 12px;
  font-weight: 700;
}

.hot-main {
  display: grid;
  gap: 3px;
  min-width: 0;
}

.hot-title {
  min-width: 0;
  overflow: hidden;
  color: #e8edf4;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.hot-category {
  color: var(--agent-muted);
  font-size: 12px;
}

.hot-item svg {
  color: #6f7b8b;
}

.hot-state {
  padding: 24px 0;
  color: var(--agent-muted);
  font-size: 13px;
  text-align: center;
}

.message-row {
  display: flex;
  gap: 12px;
  max-width: 840px;
  margin: 0 auto 24px;
  align-items: flex-start;
}

.message-row.user {
  flex-direction: row-reverse;
}

.message-avatar {
  flex: 0 0 34px;
  width: 34px;
  height: 34px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 700;
}

.message-avatar.user {
  display: grid;
  place-items: center;
  background: var(--agent-primary);
  color: #fff;
}

.message-content {
  max-width: min(720px, calc(100% - 52px));
  display: grid;
  gap: 6px;
}

.message-row.user .message-content {
  justify-items: end;
}

.message-author {
  color: var(--agent-muted);
  font-size: 12px;
}

.message-bubble {
  max-width: 100%;
  padding: 12px 14px;
  border: 1px solid var(--agent-border);
  border-radius: 8px;
  background: var(--agent-surface);
  color: #e8edf4;
  white-space: pre-wrap;
  line-height: 1.68;
  overflow-wrap: anywhere;
}

.message-row.user .message-bubble {
  background: #245fd1;
  border-color: #3476ea;
  color: #fff;
}

.message-link {
  color: #8cc7ff;
  text-decoration: underline;
  text-underline-offset: 3px;
  word-break: break-all;
}

.message-link:hover {
  color: #cfe7ff;
}

.qr-link-card {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  margin: 8px 0 2px;
  padding: 12px;
  border: 1px solid #d8dee8;
  border-radius: 8px;
  background: #f8fafc;
  vertical-align: top;
  white-space: normal;
}

.qr-image-wrap {
  width: 180px;
  height: 180px;
  display: grid;
  place-items: center;
  border-radius: 6px;
  background: #fff;
}

.qr-image {
  width: 180px;
  height: 180px;
  display: block;
}

.qr-caption {
  color: #1f2937;
  font-size: 13px;
  line-height: 1.3;
}

.qr-loading {
  width: 26px;
  height: 26px;
  border: 3px solid #d1d5db;
  border-top-color: var(--agent-primary);
  border-radius: 50%;
  animation: spin .8s linear infinite;
}

.inline-loading {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  min-width: 42px;
  min-height: 18px;
}

.inline-loading span {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #aeb6c2;
  animation: blink 1.2s infinite;
}

.inline-loading span:nth-child(2) {
  animation-delay: .15s;
}

.inline-loading span:nth-child(3) {
  animation-delay: .3s;
}

.stream-cursor {
  display: inline-block;
  width: 7px;
  height: 16px;
  margin-left: 3px;
  border-radius: 2px;
  background: var(--agent-accent);
  vertical-align: -2px;
  animation: cursorBlink 1s infinite;
}

.composer {
  position: sticky;
  bottom: 16px;
  display: grid;
  grid-template-columns: 34px minmax(0, 1fr) auto;
  align-items: end;
  gap: 10px;
  margin-bottom: 18px;
  padding: 12px;
  border: 1px solid var(--agent-border-strong);
  border-radius: 8px;
  background: rgba(23, 25, 31, .98);
  box-shadow: 0 16px 40px rgba(0, 0, 0, .28);
}

.composer-icon {
  width: 34px;
  height: 34px;
  display: grid;
  place-items: center;
  border-radius: 8px;
  background: var(--agent-surface-soft);
  color: #8cc7ff;
}

.composer textarea {
  width: 100%;
  min-height: 34px;
  max-height: 128px;
  resize: none;
  border: 0;
  outline: 0;
  background: transparent;
  color: #fff;
  font: inherit;
  line-height: 1.5;
  padding: 6px 0;
}

.composer textarea::placeholder {
  color: #737d8c;
}

.composer textarea:disabled {
  cursor: not-allowed;
  opacity: .7;
}

.send-button {
  min-height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 0 14px;
  background: var(--agent-primary);
  border-color: var(--agent-primary);
  color: #fff;
  font-weight: 650;
}

.send-button:hover:not(:disabled) {
  background: var(--agent-primary-hover);
  border-color: var(--agent-primary-hover);
}

.send-button:disabled {
  background: #334155;
  border-color: #3f4a5a;
  color: #8f9aad;
  cursor: not-allowed;
}

@keyframes blink {
  0%, 80%, 100% { opacity: .25; }
  40% { opacity: 1; }
}

@keyframes cursorBlink {
  0%, 45% { opacity: 1; }
  46%, 100% { opacity: .1; }
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

@media (max-width: 720px) {
  .agent-header-inner {
    width: calc(100% - 24px);
    min-height: 64px;
    grid-template-columns: auto 1fr auto;
    gap: 8px;
  }

  .agent-title {
    justify-content: flex-start;
  }

  .brand-icon {
    display: none;
  }

  .agent-title h1 {
    font-size: 17px;
  }

  .agent-title p {
    display: none;
  }

  .toolbar-button {
    min-height: 34px;
    padding: 0 9px;
    font-size: 12px;
  }

  .agent-main {
    width: calc(100% - 20px);
  }

  .conversation {
    height: calc(100vh - 180px);
    min-height: 380px;
    padding-top: 24px;
  }

  .welcome {
    margin-top: 8vh;
  }

  .welcome h2 {
    font-size: 22px;
  }

  .hot-list {
    grid-template-columns: 1fr;
  }

  .message-row {
    gap: 9px;
    margin-bottom: 20px;
  }

  .message-avatar {
    flex-basis: 32px;
    width: 32px;
    height: 32px;
  }

  .message-content {
    max-width: calc(100% - 44px);
  }

  .message-bubble {
    font-size: 14px;
    padding: 10px 12px;
  }

  .qr-link-card {
    display: none;
  }

  .composer {
    bottom: 10px;
    grid-template-columns: minmax(0, 1fr) auto;
    padding: 10px;
  }

  .composer-icon {
    display: none;
  }
}

@media (max-width: 420px) {
  .toolbar-button span {
    display: none;
  }

  .toolbar-button {
    width: 36px;
    padding: 0;
  }

  .send-button span {
    display: none;
  }

  .send-button {
    width: 38px;
    padding: 0;
  }
}
</style>
